import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeSlash, GoogleLogo, Warning } from '@phosphor-icons/react';

function formatAxiosError(err, action = "Sign-up") {
  // Network error — request never reached the server (CORS, edge timeout,
  // ECONNRESET, DNS, offline). Without this branch, users see a generic
  // "Something went wrong" instead of the real failure cause.
  if (!err.response) {
    return `Connection failed: ${err.message || 'no response from server'}. Please check your network and try again.`;
  }
  const detail = err.response.data?.detail;
  if (detail != null) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const joined = detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ");
      if (joined) return joined;
    }
    if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
    const stringified = String(detail);
    if (stringified && stringified !== "[object Object]") return stringified;
  }
  return `${action} failed (HTTP ${err.response.status}). Please try again — if it keeps happening, contact support with this code.`;
}

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatAxiosError(err, "Sign-up"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9fb] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-bold text-3xl text-[#2d3338]">LifeAdmin</h1>
            <p className="label-md mt-2">AI Chief of Staff</p>
          </Link>
        </div>

        {/* Register Card */}
        <div className="surface-card p-8">
          <h2 className="headline-md mb-2">Create account</h2>
          <p className="body-lg mb-8">Let's get your life together. Literally.</p>

          {error && (
            <div className="mb-6 p-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: 'var(--error-container)' }}>
              <Warning size={24} className="text-[#9d1f2d] flex-shrink-0" weight="fill" />
              <p className="text-[#410002] text-sm font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="label-md block mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field"
                placeholder="What should we call you?"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="label-md block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="your@email.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="label-md block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-12"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#596065] hover:text-[#2d3338] transition-colors"
                >
                  {showPassword ? <EyeSlash size={22} /> : <Eye size={22} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#ebeef2]"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 label-md">Or continue with</span>
            </div>
          </div>

          <button
            onClick={loginWithGoogle}
            className="btn-secondary w-full flex items-center justify-center gap-3"
            data-testid="google-register-btn"
          >
            <GoogleLogo size={22} weight="bold" />
            Sign up with Google
          </button>

          <p className="text-center mt-8 body-lg">
            Already have an account?{' '}
            <Link to="/login" className="text-[#005bc1] font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
